import "../pushNotification/styles.scss";
import "./styles.scss";
import { useHistory } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getPushScheduleProvidersAPI,
  getPushScheduleTimezonesAPI,
  getPushSchedulesAPI,
  createPushScheduleAPI,
  updatePushScheduleAPI,
  setPushScheduleActiveAPI,
  deletePushScheduleAPI,
} from "../api/ClientApi";
import { useDispatch } from "react-redux";
import { handleLogoutRedux } from "../../redux/actions/userAction";
import { Confirm } from "react-admin";
import {
  ClockIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  AppsIcon,
} from "../icons";

const CONTENT_SOURCE_OPTIONS = [
  { value: "manual", label: "Nhập tay" },
  { value: "api_fetch", label: "Lấy từ API định kỳ" },
];

const TARGET_OPTIONS = [
  { value: "app", label: "Tất cả thiết bị" },
  { value: "user", label: "Theo User ID" },
  { value: "device", label: "Theo Device ID" },
  { value: "timezone", label: "Theo Timezone" },
];

const SCHEDULE_TYPE_OPTIONS = [
  { value: "once", label: "Chạy 1 lần" },
  { value: "recurring", label: "Lặp lại" },
];

const RECURRING_MODE_OPTIONS = [
  { value: "cron", label: "Giờ UTC cố định (cron)" },
  { value: "local_hours", label: "Giờ local theo thiết bị" },
];

const TARGET_LABELS = {
  app: "Tất cả thiết bị",
  user: "User ID",
  device: "Device ID",
  timezone: "Timezone",
};

const ENVIRONMENT_OPTIONS = [
  { value: "production", label: "Production" },
  { value: "sandbox", label: "Sandbox" },
];

const ENVIRONMENT_LABELS = {
  production: "Production",
  sandbox: "Sandbox",
};

const emptyForm = {
  id: null,
  name: "",
  environment: "production",
  contentSource: "manual",
  title: "",
  body: "",
  dataJson: "",
  providerKey: "",
  targetType: "app",
  targetValue: "",
  scheduleType: "once",
  runAt: "",
  recurringMode: "cron",
  cronExpression: "",
  localHours: "",
};

const ChipGroup = ({ options, value, onChange, ariaLabel }) => (
  <div className="push-chips" role="group" aria-label={ariaLabel}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        className={`push-chip${value === option.value ? " is-active" : ""}`}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

// Chuyển ISO string (server) <-> value cho <input type="datetime-local"> (giờ local trình duyệt).
const toDatetimeLocalValue = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const describeSchedule = (item) => {
  if (item.schedule_type === "once") {
    return `1 lần lúc ${item.run_at ? new Date(item.run_at).toLocaleString() : "—"}`;
  }
  if (item.recurring_mode === "cron") {
    return `Lặp lại — cron: ${item.cron_expression} (UTC)`;
  }
  return `Lặp lại — giờ local: ${item.local_hours}`;
};

const describeFormSchedule = (form) => {
  if (form.scheduleType === "once") {
    if (!form.runAt) return "Chưa chọn thời điểm chạy";
    return `Chạy 1 lần lúc ${new Date(form.runAt).toLocaleString()}`;
  }
  if (form.recurringMode === "cron") {
    return form.cronExpression
      ? `Lặp lại — cron: ${form.cronExpression} (UTC)`
      : "Chưa nhập cron expression";
  }
  return form.localHours
    ? `Lặp lại — giờ local: ${form.localHours}`
    : "Chưa nhập giờ local";
};

const PushSchedule = (props) => {
  const [appId, setAppId] = useState("");
  const [providers, setProviders] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [reload, setReload] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    const app = props.location.state;
    if (!app) {
      history.push("/admin");
      return;
    }
    setAppId(app.app_id);
    getPushScheduleProvidersAPI().then((response) => {
      if (response.errorCode === 0) {
        setProviders(response.data || []);
      }
    });
    getPushScheduleTimezonesAPI({ appId: app.app_id }).then((response) => {
      if (response.errorCode === 0) {
        setTimezones(response.data || []);
      }
    });
  }, []);

  useEffect(() => {
    if (reload && appId) {
      getPushSchedulesAPI({ appId }).then((response) => {
        setReload(false);
        const { errorCode, message, data } = response;
        if (errorCode === 0) {
          setSchedules(data);
        } else if (errorCode === 401) {
          dispatch(handleLogoutRedux());
        } else {
          toast.error(message);
        }
      });
    }
  }, [reload, appId, dispatch]);

  // Ở chế độ "Giờ local theo thiết bị", dispatch luôn tự quét timezone của thiết bị — target
  // (user/device/timezone) không áp dụng, kể cả khi form.targetType còn giữ giá trị cũ từ lúc
  // trước khi admin đổi sang chế độ này (UI đã ẩn phần chọn target nhưng state vẫn còn).
  const isLocalHoursMode = form.scheduleType === "recurring" && form.recurringMode === "local_hours";

  const resetForm = () => {
    setForm(emptyForm);
    setIsEditing(false);
  };

  const invalidInputs = () => {
    if (!form.name) {
      toast.error("Name is not empty!");
      return false;
    }
    if (form.contentSource === "manual") {
      if (!form.title || !form.body) {
        toast.error("Title/Body is not empty!");
        return false;
      }
      if (form.dataJson) {
        try {
          JSON.parse(form.dataJson);
        } catch (e) {
          toast.error("Data must be a valid JSON!");
          return false;
        }
      }
    } else if (!form.providerKey) {
      toast.error("Chọn 1 provider!");
      return false;
    }
    if (
      !isLocalHoursMode &&
      ["user", "device", "timezone"].includes(form.targetType) &&
      !form.targetValue
    ) {
      toast.error("Target value is not empty!");
      return false;
    }
    if (form.scheduleType === "once") {
      if (!form.runAt) {
        toast.error("Chọn thời điểm chạy!");
        return false;
      }
    } else if (form.recurringMode === "cron") {
      if (!form.cronExpression) {
        toast.error("Nhập cron expression!");
        return false;
      }
    } else if (!form.localHours) {
      toast.error("Nhập danh sách giờ local (vd: 8,12,19)!");
      return false;
    }
    return true;
  };

  const buildPayload = () => ({
    id: form.id || undefined,
    appId,
    name: form.name,
    environment: form.environment,
    contentSource: form.contentSource,
    title: form.contentSource === "manual" ? form.title : undefined,
    body: form.contentSource === "manual" ? form.body : undefined,
    data: form.contentSource === "manual" && form.dataJson ? JSON.parse(form.dataJson) : undefined,
    providerKey: form.contentSource === "api_fetch" ? form.providerKey : undefined,
    targetType: isLocalHoursMode ? "app" : form.targetType,
    targetValue:
      !isLocalHoursMode && ["user", "device", "timezone"].includes(form.targetType)
        ? form.targetValue
        : undefined,
    scheduleType: form.scheduleType,
    runAt: form.scheduleType === "once" ? new Date(form.runAt).toISOString() : undefined,
    recurringMode: form.scheduleType === "recurring" ? form.recurringMode : undefined,
    cronExpression:
      form.scheduleType === "recurring" && form.recurringMode === "cron" ? form.cronExpression : undefined,
    localHours:
      form.scheduleType === "recurring" && form.recurringMode === "local_hours" ? form.localHours : undefined,
  });

  const handleSave = () => {
    if (!invalidInputs()) {
      return;
    }
    setSaving(true);
    const request = isEditing ? updatePushScheduleAPI(buildPayload()) : createPushScheduleAPI(buildPayload());
    request
      .then((response) => {
        const { errorCode, message } = response;
        if (errorCode === 0) {
          toast.success(message);
          resetForm();
          setReload(true);
        } else if (errorCode === 401) {
          toast.error(message);
          dispatch(handleLogoutRedux());
        } else {
          toast.error(message);
        }
      })
      .finally(() => setSaving(false));
  };

  const handleEdit = (item) => {
    setIsEditing(true);
    setForm({
      id: item.id,
      name: item.name,
      environment: item.environment || "production",
      contentSource: item.content_source,
      title: item.title || "",
      body: item.body || "",
      dataJson: item.data || "",
      providerKey: item.provider_key || "",
      targetType: item.target_type,
      targetValue: item.target_value || "",
      scheduleType: item.schedule_type,
      runAt: toDatetimeLocalValue(item.run_at),
      recurringMode: item.recurring_mode || "cron",
      cronExpression: item.cron_expression || "",
      localHours: item.local_hours || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleToggleActive = (item) => {
    setPushScheduleActiveAPI({ id: item.id, isActive: !item.is_active }).then((response) => {
      const { errorCode, message } = response;
      if (errorCode === 0) {
        setReload(true);
      } else if (errorCode === 401) {
        toast.error(message);
        dispatch(handleLogoutRedux());
      } else {
        toast.error(message);
      }
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) {
      return;
    }
    const id = deleteTarget;
    setDeleteTarget(null);
    deletePushScheduleAPI({ id }).then((response) => {
      const { errorCode, message } = response;
      if (errorCode === 0) {
        toast.success(message);
        if (isEditing && form.id === id) {
          resetForm();
        }
        setReload(true);
      } else if (errorCode === 401) {
        toast.error(message);
        dispatch(handleLogoutRedux());
      } else {
        toast.error(message);
      }
    });
  };

  const totalSchedules = schedules.length;
  const activeSchedules = schedules.filter((s) => s.is_active).length;
  const inactiveSchedules = totalSchedules - activeSchedules;

  const summaryContent =
    form.contentSource === "manual"
      ? form.title || "Chưa có tiêu đề"
      : form.providerKey
        ? `API: ${form.providerKey}`
        : "Chưa chọn provider";

  const summaryTarget = isLocalHoursMode
    ? "Theo timezone thiết bị"
    : form.targetType === "app"
      ? TARGET_LABELS.app
      : `${TARGET_LABELS[form.targetType] || form.targetType}${
          form.targetValue ? ` · ${form.targetValue}` : ""
        }`;

  return (
    <div className="page-container push-page schedule-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Push Schedule</h1>
          <p className="page-subtitle">{appId}</p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => history.push("/admin")}
        >
          ← Quay lại App Manager
        </button>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-card-icon">
            <ClockIcon />
          </div>
          <div>
            <div className="stat-card-value">{totalSchedules}</div>
            <div className="stat-card-label">Tổng lịch</div>
          </div>
        </div>
        <div className="stat-card stat-card--success">
          <div className="stat-card-icon">
            <CheckCircleIcon />
          </div>
          <div>
            <div className="stat-card-value">{activeSchedules}</div>
            <div className="stat-card-label">Đang Active</div>
          </div>
        </div>
        <div className="stat-card stat-card--warning">
          <div className="stat-card-icon">
            <AppsIcon />
          </div>
          <div>
            <div className="stat-card-value">{inactiveSchedules}</div>
            <div className="stat-card-label">Inactive</div>
          </div>
        </div>
      </div>

      <div className="push-compose">
        <div className="push-compose__form">
          <div className="push-panel">
            <div className="form-section-title d-flex align-items-center gap-2">
              <ClockIcon width={16} height={16} />
              {isEditing ? `Cập nhật lịch #${form.id}` : "Tạo lịch mới"}
            </div>

            <div className="mb-3">
              <label className="form-label">Tên lịch</label>
              <input
                className="form-control"
                type="text"
                placeholder="VD: Tin audio buổi sáng"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Môi trường</label>
              <ChipGroup
                ariaLabel="Môi trường"
                options={ENVIRONMENT_OPTIONS}
                value={form.environment}
                onChange={(value) => setForm({ ...form, environment: value })}
              />
              <div className="form-text">
                Sandbox dùng project Firebase riêng (nếu app đã cấu hình) — dùng để test lịch
                push an toàn trước khi chuyển sang Production.
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Nguồn nội dung</label>
              <ChipGroup
                ariaLabel="Nguồn nội dung"
                options={CONTENT_SOURCE_OPTIONS}
                value={form.contentSource}
                onChange={(value) => setForm({ ...form, contentSource: value })}
              />
            </div>

            {form.contentSource === "manual" ? (
              <>
                <div className="form-section-title">Nội dung</div>
                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Tiêu đề thông báo"
                    value={form.title}
                    onChange={(event) => setForm({ ...form, title: event.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Body</label>
                  <textarea
                    className="form-control"
                    placeholder="Nội dung thông báo"
                    rows={3}
                    value={form.body}
                    onChange={(event) => setForm({ ...form, body: event.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Data (optional JSON)</label>
                  <textarea
                    className="form-control"
                    placeholder='Ví dụ: {"screen":"home"}'
                    rows={2}
                    value={form.dataJson}
                    onChange={(event) => setForm({ ...form, dataJson: event.target.value })}
                  />
                </div>
              </>
            ) : (
              <div className="mb-3">
                <label className="form-label">Provider</label>
                <select
                  className="form-select"
                  value={form.providerKey}
                  onChange={(event) => setForm({ ...form, providerKey: event.target.value })}
                >
                  <option value="">-- Chọn provider --</option>
                  {providers.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  Nội dung push sẽ được tự động lấy từ nguồn API tương ứng ngay khi đến giờ chạy.
                </div>
              </div>
            )}

            <div className="form-section-title">Đối tượng & lịch</div>

            {isLocalHoursMode ? (
              <div className="mb-3">
                <label className="form-label">Gửi tới</label>
                <div className="schedule-info-note">
                  Chế độ &quot;Giờ local theo thiết bị&quot; tự quét mọi timezone đang có thiết bị
                  đăng ký và gửi riêng cho từng nhóm theo đúng giờ local — không cần chọn target.
                </div>
              </div>
            ) : (
              <div className="mb-3">
                <label className="form-label">Gửi tới</label>
                <ChipGroup
                  ariaLabel="Đối tượng gửi"
                  options={TARGET_OPTIONS}
                  value={form.targetType}
                  onChange={(value) => setForm({ ...form, targetType: value, targetValue: "" })}
                />
              </div>
            )}

            {!isLocalHoursMode && form.targetType === "timezone" ? (
              <div className="mb-3">
                <label className="form-label">Timezone</label>
                {timezones.length > 0 ? (
                  <select
                    className="form-select"
                    value={form.targetValue}
                    onChange={(event) => setForm({ ...form, targetValue: event.target.value })}
                  >
                    <option value="">-- Chọn timezone --</option>
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="VD: Asia/Ho_Chi_Minh"
                      value={form.targetValue}
                      onChange={(event) => setForm({ ...form, targetValue: event.target.value })}
                    />
                    <div className="form-text">
                      Chưa có thiết bị nào của app này đăng ký timezone — nhập tay theo chuẩn IANA
                      (vd: Asia/Ho_Chi_Minh, America/New_York).
                    </div>
                  </>
                )}
              </div>
            ) : (
              !isLocalHoursMode &&
              form.targetType !== "app" && (
                <div className="mb-3">
                  <label className="form-label">{TARGET_LABELS[form.targetType]}</label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder={TARGET_LABELS[form.targetType]}
                    value={form.targetValue}
                    onChange={(event) => setForm({ ...form, targetValue: event.target.value })}
                  />
                </div>
              )
            )}

            <div className="mb-3">
              <label className="form-label">Loại lịch</label>
              <ChipGroup
                ariaLabel="Loại lịch"
                options={SCHEDULE_TYPE_OPTIONS}
                value={form.scheduleType}
                onChange={(value) => setForm({ ...form, scheduleType: value })}
              />
            </div>

            {form.scheduleType === "once" ? (
              <div className="mb-3">
                <label className="form-label">Thời điểm chạy</label>
                <input
                  className="form-control"
                  type="datetime-local"
                  value={form.runAt}
                  onChange={(event) => setForm({ ...form, runAt: event.target.value })}
                />
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <label className="form-label">Kiểu lặp lại</label>
                  <ChipGroup
                    ariaLabel="Kiểu lặp lại"
                    options={RECURRING_MODE_OPTIONS}
                    value={form.recurringMode}
                    onChange={(value) => setForm({ ...form, recurringMode: value })}
                  />
                </div>
                {form.recurringMode === "cron" ? (
                  <div className="mb-3">
                    <label className="form-label">Cron expression (giờ UTC)</label>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="VD: 0 8 * * * (8h UTC mỗi ngày)"
                      value={form.cronExpression}
                      onChange={(event) =>
                        setForm({ ...form, cronExpression: event.target.value })
                      }
                    />
                  </div>
                ) : (
                  <div className="mb-3">
                    <label className="form-label">Giờ local (0-23, phân cách bởi dấu phẩy)</label>
                    <input
                      className="form-control"
                      type="text"
                      placeholder="VD: 8,12,19"
                      value={form.localHours}
                      onChange={(event) => setForm({ ...form, localHours: event.target.value })}
                    />
                  </div>
                )}
              </>
            )}

            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-primary d-flex align-items-center gap-2"
                disabled={saving}
                onClick={handleSave}
              >
                <PlusIcon width={16} height={16} />
                {saving ? "Đang lưu..." : isEditing ? "Cập nhật lịch" : "Tạo lịch"}
              </button>
              {isEditing && (
                <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                  Huỷ
                </button>
              )}
            </div>
          </div>
        </div>

        <aside className="push-compose__preview">
          <div className="push-panel schedule-summary">
            <div className="form-section-title">Tóm tắt lịch</div>
            <div className="schedule-summary__name">{form.name || "Chưa đặt tên"}</div>
            <div className="schedule-summary__meta">
              <span
                className={`badge rounded-pill text-bg-${
                  form.environment === "sandbox" ? "warning" : "dark"
                }`}
              >
                {ENVIRONMENT_LABELS[form.environment]}
              </span>
              <span className="badge rounded-pill text-bg-secondary">
                {form.contentSource === "manual" ? "Nhập tay" : "API định kỳ"}
              </span>
            </div>
            <dl className="schedule-summary__list">
              <dt>Nội dung</dt>
              <dd>{summaryContent}</dd>
              <dt>Gửi tới</dt>
              <dd>{summaryTarget}</dd>
              <dt>Lịch chạy</dt>
              <dd>{describeFormSchedule(form)}</dd>
            </dl>
          </div>
        </aside>
      </div>

      <div className="push-history">
        <div className="push-history__header">
          <h2 className="push-history__title">Danh sách lịch</h2>
          <span className="text-muted small">{schedules.length} lịch</span>
        </div>

        <div className="push-history__list">
          {schedules.map((item) => {
            const isActive = !!item.is_active;
            return (
              <div
                key={item.id}
                className={`schedule-row${isActive ? "" : " schedule-row--inactive"}`}
              >
                <div className="schedule-row__main">
                  <div className="schedule-row__title-line">
                    <span className="schedule-row__name">{item.name}</span>
                    <span
                      className={`badge rounded-pill text-bg-${
                        item.environment === "sandbox" ? "warning" : "dark"
                      }`}
                    >
                      {ENVIRONMENT_LABELS[item.environment] || "Production"}
                    </span>
                    {item.content_source === "manual" ? (
                      <span className="badge rounded-pill text-bg-secondary">Nhập tay</span>
                    ) : (
                      <span className="badge rounded-pill text-bg-info">
                        API: {item.provider_key}
                      </span>
                    )}
                    {isActive ? (
                      <span className="badge rounded-pill text-bg-success">Active</span>
                    ) : (
                      <span className="badge rounded-pill text-bg-secondary">Inactive</span>
                    )}
                  </div>
                  <div className="schedule-row__desc">{describeSchedule(item)}</div>
                  <div className="schedule-row__sub">
                    {item.recurring_mode === "local_hours" ? (
                      <span>Theo timezone thiết bị</span>
                    ) : (
                      <span>
                        {TARGET_LABELS[item.target_type] || item.target_type}
                        {item.target_value ? ` · ${item.target_value}` : ""}
                      </span>
                    )}
                    <span>
                      · Lần chạy gần nhất:{" "}
                      {item.last_run_at
                        ? new Date(item.last_run_at).toLocaleString()
                        : "Chưa chạy"}
                    </span>
                  </div>
                </div>
                <div className="schedule-row__side">
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={isActive}
                      onChange={() => handleToggleActive(item)}
                      title={isActive ? "Tắt lịch" : "Bật lịch"}
                    />
                  </div>
                  <button
                    type="button"
                    className="icon-btn"
                    title="Sửa lịch"
                    onClick={() => handleEdit(item)}
                  >
                    <PencilIcon width={16} height={16} />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    title="Xoá lịch"
                    onClick={() => setDeleteTarget(item.id)}
                  >
                    <TrashIcon width={16} height={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {schedules.length === 0 && (
          <div className="empty-state">Chưa có lịch push nào cho app này.</div>
        )}
      </div>

      <Confirm
        isOpen={!!deleteTarget}
        loading={false}
        title=""
        content="Xác nhận xoá lịch push này? Hành động này không thể hoàn tác."
        confirm="Xoá"
        confirmColor="warning"
        cancel="Huỷ bỏ"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default PushSchedule;
